import Oidc from 'oidc-client'
import Vue from 'vue'

const origin = window.location.origin
const baseUrl = window.config.BaseUrl

var mgr = new Oidc.UserManager({
  userStore: new Oidc.WebStorageStateStore({ store: window.localStorage }),
  authority: window.config.SmartIdUrl,
  client_id: window.config.client_id,
  redirect_uri: origin + baseUrl + '/callback.html',
  response_type: 'code',
  scope: window.config.scopes,
  post_logout_redirect_uri: origin + baseUrl + '/callbacksignout.html' ,
  silent_redirect_uri: origin + baseUrl + '/silent-renew.html',
  accessTokenExpiringNotificationTime: 20,
  automaticSilentRenew: false,
  filterProtocolClaims: true,
  loadUserInfo: true,
  clockSkew: 300
})
Oidc.Log.logger = window.config?.node_env !== 'production' ? console : {
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: () => {},
}
Oidc.Log.level = window.config?.node_env !== 'production' ? Oidc.Log.DEBUG : Oidc.Log.ERROR

let countError = 0
let renewInProgress = null

mgr.events.addUserLoaded(function () {
  countError = 0
  renewInProgress = null
})

mgr.events.addUserSignedOut(function () {
  Vue.prototype.$log('UserSignedOut')
})

mgr.events.addAccessTokenExpiring(function () {
  Vue.prototype.$log('AccessTokenExpiring — renewing')
  AuthModule._doRenew()
})

mgr.events.addAccessTokenExpired(function () {
  Vue.prototype.$log('AccessTokenExpired — redirecting to login')
  Vue.prototype.$auth.signIn()
})

export default class AuthModule {
  /**
   * Single-point silent renew with deduplication.
   * All concurrent callers share the same promise.
   */
  static _doRenew() {
    if (renewInProgress) {
      return renewInProgress
    }
    renewInProgress = mgr.signinSilent()
      .then(user => {
        countError = 0
        renewInProgress = null
        return user
      })
      .catch(err => {
        countError += 1
        renewInProgress = null
        throw err
      })
    return renewInProgress
  }

  renewToken(silentMode) {
    return AuthModule._doRenew()
      .then(user => {
        if (user == null) {
          this.signIn()
          return null
        }
        return user
      })
      .catch(err => {
        Vue.prototype.$sinfo('Silent renew error: ', err)
        if (countError >= 3) {
          this.signIn()
          return null
        }
        if (silentMode) {
          return null
        }
        throw err
      })
  }

  async tokenExpired() {
    return (await mgr.getUser()).expired
  }

  async silentRrenewToken() {
    await Vue.prototype.$auth.renewToken(true)
    await mgr.clearStaleState()
  }

  //Get the user who is logged in
  getUser() {
    let {$notifier: notifier} = Vue.prototype
    return new Promise((resolve, reject) => {
      mgr.getUser().then(function (user) {
        notifier.checHub()
        return resolve(user)
      }).catch(function (err) {
        Vue.prototype.$log_error(err)
        return reject(err)
      })
    })
  }

  getUserId() {
    return new Promise((resolve, reject) => {
      mgr.getUser().then(function (user) {
        let id = user.profile.preferred_username
        return resolve(id[1])
      }).catch(function (err) {
        Vue.prototype.$log_error(err)
        return reject(err)
      })
    })
  }

  // Redirect of the current window to the authorization endpoint.
  signIn() {
    if (countError >= 200) {
      //window.open(window.config.SmartIdUrl + `/userprofile?returnUrl=${window.location.href}&client_id=s.smarttender.biz`, '_self')
      this.signOut()
      return
    }
    let redirectUrl = window.location.href.endsWith('/login') ? window.location.href.replace('/login','') : window.location.href
    mgr.signinRedirect({ state: { returnUrl: redirectUrl }, ui_locales: sessionStorage.languageCode })
      .then(() => {
        countError = 0
      })
      .catch(function (err) {
        countError += 1
        Vue.prototype.$log_error(err)
      })
  }

  // Redirect of the current window to the end session endpoint
  async signOut() {
    await Vue.prototype.$store.dispatch('LOGOUT')
    let href = window.location.href,
      redirectUrl = href.endsWith('/login') ? href.replace('/login','')
        : href.includes('error') ? href.slice(0, href.indexOf('error')) : href
    await mgr.signoutRedirect({ state: { returnUrl: redirectUrl }, ui_locales: sessionStorage.languageCode })

      .catch(function (err) {
        Vue.prototype.$log_error(err)
      })
    await mgr.clearStaleState()
  }

  async getUserToken() {
    const isTokenExpired = await Vue.prototype.$auth.tokenExpired()
    if (isTokenExpired) {
      try {
        await this.renewToken()
      } catch (err) {
        return null
      }
    }
    const user = await Vue.prototype.$auth.getUser()
    return user.access_token
  }
}
